import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink, ChevronDown, FileText, BookOpen, GraduationCap, Menu, X } from "lucide-react";
import { QOVARIX_NAVBAR_LOGO_PATH } from "@/constants/assets";

const DOCS_ITEMS = [
  { label: "Whitepaper", href: "/docs/whitepaper", icon: FileText, desc: "Full protocol specification" },
  { label: "Overview", href: "/docs/overview", icon: BookOpen, desc: "Protocol summary" },
  { label: "Learn", href: "/docs/learn", icon: GraduationCap, desc: "How to trade on Qovarix" },
];

export default function Navbar({ onLaunch, onLaunchApp }) {
  const [docsOpen, setDocsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropRef = useRef(null);
  const location = useLocation();
  const isLanding = location.pathname === "/";

  useEffect(() => {
    const close = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDocsOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLaunchClick = () => {
    setMobileOpen(false);
    onLaunch?.();
  };

  const handleAppLaunchClick = () => {
    setMobileOpen(false);
    onLaunchApp?.();
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 sm:px-6 lg:px-10 py-2"
        style={{ background: "rgba(3, 4, 6, 0.55)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
      >
        <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo">
          <img
            src={QOVARIX_NAVBAR_LOGO_PATH}
            alt="Qovarix"
            width={240}
            height={64}
            className="w-[188px] sm:w-[220px] md:w-[236px] h-auto max-h-11 object-contain object-left"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          <div ref={dropRef} className="relative">
            <button
              data-testid="nav-docs-trigger"
              onClick={() => setDocsOpen(!docsOpen)}
              className="font-grotesk text-sm text-slate-400 hover:text-white transition-colors duration-200 flex items-center gap-1"
            >
              Docs
              <ChevronDown size={13} className={`transition-transform duration-200 ${docsOpen ? "rotate-180" : ""}`} />
            </button>
            {docsOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 py-2 rounded-lg"
                style={{ background: "rgba(8, 10, 16, 0.92)", backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                {DOCS_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.label}
                      to={item.href}
                      data-testid={`nav-docs-${item.label.toLowerCase()}`}
                      onClick={() => setDocsOpen(false)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors"
                    >
                      <Icon size={14} className="text-[#00F0FF] mt-0.5 shrink-0" strokeWidth={1.5} />
                      <div>
                        <div className="font-grotesk text-sm text-white">{item.label}</div>
                        <div className="font-mono text-[9px] text-slate-500 mt-0.5">{item.desc}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <a href="https://discord.gg/m7pPSyccnT" target="_blank" rel="noopener noreferrer" className="font-grotesk text-sm text-slate-400 hover:text-white transition-colors duration-200 flex items-center gap-1">Community</a>
          <a href="https://x.com/Qovarix" target="_blank" rel="noopener noreferrer" className="font-grotesk text-sm text-slate-400 hover:text-white transition-colors duration-200 flex items-center gap-1">
            Twitter / X <ExternalLink size={11} className="opacity-40" />
          </a>
          <a href="https://discord.gg/m7pPSyccnT" target="_blank" rel="noopener noreferrer" className="font-grotesk text-sm text-slate-400 hover:text-white transition-colors duration-200 flex items-center gap-1">
            Discord <ExternalLink size={11} className="opacity-40" />
          </a>
        </div>

        {/* Desktop CTA */}
        {isLanding ? (
          <div className="hidden md:flex items-center gap-2">
            <button data-testid="nav-launch-btn" onClick={onLaunch} className="launch-btn inline-flex items-center" style={{ padding: "8px 24px", fontSize: "11px" }}>
              Join Beta
            </button>
            <button
              data-testid="nav-open-app-btn"
              onClick={onLaunchApp}
              className="hero-launch-btn cyan-glow inline-flex items-center"
              style={{ padding: "8px 18px", fontSize: "11px" }}
            >
              Launch
            </button>
          </div>
        ) : (
          <Link to="/" className="launch-btn hidden md:inline-flex items-center" style={{ padding: "8px 24px", fontSize: "11px" }}>
            Back to Home
          </Link>
        )}

        {/* Mobile hamburger */}
        <button
          data-testid="nav-mobile-trigger"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden p-2 -mr-2 text-slate-300 hover:text-white transition-colors"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          data-testid="nav-mobile-panel"
          className="fixed inset-0 z-40 md:hidden pt-16"
          style={{ background: "rgba(3, 4, 6, 0.96)", backdropFilter: "blur(24px)" }}
          onClick={() => setMobileOpen(false)}
        >
          <div className="max-w-md mx-auto px-6 py-6" onClick={(e) => e.stopPropagation()}>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-3">Docs</span>
            <div className="flex flex-col mb-6 border-y border-white/5">
              {DOCS_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    data-testid={`nav-mobile-docs-${item.label.toLowerCase()}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-4 border-b border-white/5 last:border-0"
                  >
                    <Icon size={14} className="text-[#00F0FF] shrink-0" strokeWidth={1.5} />
                    <span className="font-grotesk text-base text-white">{item.label}</span>
                  </Link>
                );
              })}
            </div>

            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-3">Community</span>
            <div className="flex flex-col mb-6 border-y border-white/5">
              <a href="https://x.com/Qovarix" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between py-4 border-b border-white/5 font-grotesk text-base text-white">
                Twitter / X <ExternalLink size={13} className="opacity-50" />
              </a>
              <a href="https://discord.gg/m7pPSyccnT" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between py-4 font-grotesk text-base text-white">
                Discord <ExternalLink size={13} className="opacity-50" />
              </a>
            </div>

            {isLanding ? (
              <div className="grid grid-cols-1 gap-3">
                <button
                  data-testid="nav-mobile-launch-btn"
                  onClick={handleLaunchClick}
                  className="hero-launch-btn w-full justify-center"
                >
                  Join Beta
                </button>
                <button
                  data-testid="nav-mobile-open-app-btn"
                  onClick={handleAppLaunchClick}
                  className="hero-launch-btn cyan-glow w-full justify-center"
                >
                  Launch
                </button>
              </div>
            ) : (
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="hero-launch-btn w-full justify-center"
              >
                Back to Home
              </Link>
            )}
          </div>
        </div>
      )}
    </>
  );
}
